import React, { useEffect, useState } from "react";

import { generatePath, useNavigate } from "react-router-dom";

import {
  ROUTE_ARTICLES,
  ROUTE_TEMPLATES_CREATE,
  ROUTE_TEMPLATES_EDIT,
} from "../../constants/routes";
import { useTokensFinished } from "../../hooks/useTokensFinished";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useGetTemplatesQuery } from "../../redux/services/templates";
import { setUpgradeModal } from "../../redux/slices/userSlice";
import { RootState } from "../../redux/store";

import PageHeading from "../../components/common/PageHeading";
import SpringContainer from "../../components/containers/SpringContainer";
import GetStartedTemplateBanner from "../../components/features/GetStartedTemplateBanner";
import TemplateArticleFilter from "../../components/features/TemplateArticleFilter";
import Button from "../../components/form/Button";
import Input from "../../components/form/Input";
import LoadingSpinner from "../../components/loaders/LoadingSpinner";
import Table from "../../components/table/Table";
import { usePagination } from "../../hooks/usePagination";
import { Template } from "../../models";
import { useGetAccountTagsQuery } from "../../redux/services/accountTags";
import { getTemplateColumns } from "./components/table/templateColumns";

const limit = 20;

const Templates = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isTokensFinished } = useTokensFinished();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [searchTotalTemplate, setSearchTotalTemplate] = useState(0);
  const [offset, setOffset] = useState(0);
  const [showAutoEnabled, setShowAutoEnabled] = useState<null | boolean>();
  const [accountTagIds, setAccountTagIds] = useState<string[]>([]);

  const selectedAccount = useAppSelector(
    (state: RootState) => state.user.selectedAccount
  );
  const accountId = selectedAccount?.accountId || null;

  const { data, isLoading, refetch, isFetching, isUninitialized } =
    useGetTemplatesQuery(
      {
        accountId,
        offset: offset,
        limit: limit,
        search: debouncedSearch || "",
        showAutoEnabled,
        accountTagIds,
        includeTotalCount: true,
      },
      { skip: !accountId }
    );

  const { data: accountTagData } = useGetAccountTagsQuery(
    {
      accountId,
      offset: 0,
      limit: 100,
    },
    { skip: !accountId }
  );

  useEffect(() => {
    if (accountId) {
      refetch();
    }
  }, [refetch, accountId, offset, showAutoEnabled, accountTagIds]);

  useEffect(() => {
    if (!totalTemplates && data?.totalCount !== undefined) {
      setTotalTemplates(data.totalCount);
    }
  }, [data?.totalCount, totalTemplates, setTotalTemplates]);

  useEffect(() => {
    if (debouncedSearch && data?.totalCount !== undefined) {
      setSearchTotalTemplate(data.totalCount);
    } else {
      setSearchTotalTemplate(0);
    }
  }, [data?.totalCount, debouncedSearch, setSearchTotalTemplate]);

  const templates = data?.data || [];
  const templatesExists = Boolean(templates?.length > 0);

  const { paginationData } = usePagination({
    total: data?.totalCount,
    offset,
    setOffset,
    limit,
    showPagination: debouncedSearch
      ? searchTotalTemplate > limit
      : totalTemplates > limit,
  });

  if (isUninitialized || isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <LoadingSpinner size="32px" />
      </div>
    );
  }

  const handleViewArticle = (templateName: string) =>
    navigate(ROUTE_ARTICLES, {
      state: { templateName },
    });

  const handleViewTemplate = (id: string) => {
    navigate(generatePath(ROUTE_TEMPLATES_EDIT, { id }));
  };

  const accountTags = accountTagData?.data || [];

  const filtersSelected = Boolean(
    showAutoEnabled || debouncedSearch || accountTagIds
  );
  return (
    <SpringContainer>
      <div className="bg-white h-full">
        <div className="flex justify-between items-center md:mb-4">
          <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
            <PageHeading
              heading="Article Template Settings"
              tooltip="Refresh"
              loading={isFetching}
              refetch={() => {
                refetch();
              }}
            />
            <div className="hidden md:flex items-center gap-2">
              <Input
                search
                value={search}
                placeholder="Search by title"
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                onBlur={() => setDebouncedSearch(search)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Tab") {
                    setDebouncedSearch(search);
                  }
                }}
                // loading={isFetching}
                width={350}
              />
              <TemplateArticleFilter
                {...{
                  accountTags,
                  setShowAutoEnabled,
                  accountTagIds,
                  setAccountTagIds,
                }}
              />
            </div>
          </div>
          {templatesExists && (
            <Button
              variant="primary"
              type="submit"
              onClick={() => {
                if (isTokensFinished) {
                  dispatch(setUpgradeModal(true));
                } else {
                  navigate(ROUTE_TEMPLATES_CREATE);
                }
              }}
            >
              New
            </Button>
          )}
        </div>
        <div className="flex md:hidden items-center gap-2 my-2 mb-4">
          <Input
            search
            value={search}
            placeholder="Search by title"
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            onBlur={() => setDebouncedSearch(search)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Tab") {
                setDebouncedSearch(search);
              }
            }}
          />
          <TemplateArticleFilter
            {...{
              accountTags,
              setShowAutoEnabled,
              accountTagIds,
              setAccountTagIds,
            }}
          />
        </div>
        {!templatesExists && !filtersSelected && <GetStartedTemplateBanner />}
        {!templatesExists && filtersSelected && (
          <div className="text-sm text-gray-700">
            No Article Template Settings found
          </div>
        )}
        {templatesExists && (
          <Table<Template>
            columns={getTemplateColumns(handleViewArticle)}
            data={templates}
            keyExtractor={(item) => item.templateId!}
            paginationData={paginationData}
            onRowClick={(t) => handleViewTemplate(t.templateId!)}
          />
        )}
      </div>
    </SpringContainer>
  );
};

export default Templates;
